import type { BasicStatus, PermissionType } from './enum';

export interface UserToken {
  accessToken?: string;
}

export interface UserInfo {
  id: string;
  email: string;
  role: string;
  is_enabled: boolean;
  property_id: string;
  property_ids?: string[];
  created_at: string;
  updated_at: string;
  last_active: string;
  updated_by: string | null;
  full_name: string;
  username: string;
  description: string;
}
export interface UserItem {
  id: string;
  username: string;
  full_name: string;
  email: string;
  role: string;
  is_enabled: boolean;
  property_id: string;
  property_ids?: string[];
  created_at: string;
  updated_at: string;
  last_active: string;
  updated_by: string | null;
}

export interface UserList {
  total: number;
  skip: number;
  limit: number;
  items: UserItem[];
}

export interface AnalyticsItem {
  days: string;
  property_id: string | null;
  daily_new_devices: number;
  daily_new_users: number;
  skip: number;
  limit: number;
  total_devices: number;
  active_devices: number;
  active_devices_today: number;
  active_devices_now: number;
  total_users: number;
  active_users: number;
  active_users_today: number;
  active_users_now: number;
  by_device_type: Array<{
    device_type: string;
    total_count: number;
    active_count: number;
  }>;
}
export interface AnalyticsList {
  properties?: AnalyticsItem[];
  device_type: string | null;
  active_within_days: number;
  property_id: string | null;
  skip?: number;
  limit?: number;
}
export interface AdminLogItem {
  id: string;
  admin_id: string;
  action: string;
  target_id: string;
  target_table: string;
  description: string;
  created_at: string;
  admin: UserInfo[];
}
export interface AdminLogList {
  total: number;
  skip: number;
  limit: number;
  items: AdminLogItem[];
}

export interface PropertyItem {
  id: string;
  name: string;
  is_enabled: boolean | null;
  created_at: string | null;
  created_user: User | null;
  logo_url?: string | null;
  ip_ranges: PropertyRange[] | null;
  auto_login: boolean | null;
  add_all_movies?: boolean;
  add_all_channels?: boolean;
  add_all_series?: boolean;
  add_all_cameras?: boolean;
  info_text?: string | null;
  support_phone?: string | null;
  support_qr_title?: string | null;
  support_qr_text?: string | null;
  support_qr_url?: string | null;
}

export interface PropertyRange {
  id: string;
  property_id: string;
  ip_range: string;
  description?: string;
}

export interface User {
  id: string;
  email: string;
  role?: string;
  is_enabled?: boolean;
  property_id: string | null;
  property_ids?: string[];
  full_name: string | null;
  username: string | null;
  description?: string | null;
  expired_at?: string | null;
  created_at?: string;
  updated_at?: string;
  last_active?: string;
}

export interface PropertyList {
  total: number;
  skip: number;
  limit: number;
  items: PropertyItem[];
}

export interface ChannelItem {
  id: string;
  name: string;
  number: number;
  description: string;
  logo_url: string;
  poster_url: string;
  stream_url: string;
  is_camera: boolean;
  is_dvr: boolean;
  is_enabled: boolean;
  drm_type: string | 'widevine' | 'fairplay' | 'playready' | null;
  license_url: string;
  certificate_url: string;
  gracenote_station_id: string;
  epg_feed_url: string;
  created_at: string | null;
  property_ids: string[];
  is_pinned?: boolean;
}
export interface ChannelCategoryItem {
  id: string;
  name: string;
  order_index: number;
  channels: Array<{
    id: string;
    name: string;
    number: number;
    logo_url: string;
    is_enabled: boolean;
  }>;
  channel_ids: string[];
  created_at: string | null;
  updated_at: string | null;
}
export interface ChannelCategoriesList {
  items: ChannelCategoryItem[];
  total: number;
  skip: number;
  limit: number;
}
export interface DeviceItem {
  id: string;
  device_uuid: string;
  device_name: string;
  device_model: string;
  device_model_type: string;
  device_app_id: string;
  app_version?: string | null;
  device_type: string;
  is_enabled: boolean;
  device_ip: string;
  property_id: string;
  created_at: string;
  updated_at: string;
  last_active?: string;
  user_id: string | null;
  current_user?: User | null;
  current_user_id?: string | null;

  // network information
  connection_info?: string;
  timezone?: string;
}

export interface UserAdminDetailOut {
  id: string;
  email: string;
  role: 'user' | 'admin' | 'superadmin';
  is_enabled: boolean;
  property_id: string;
  created_at: string;
  updated_at: string;
  last_active: string | null;
  updated_by: string | null;
  full_name: string | null;
  description: string | null;
  username: string;
  expired_at: string | null;
  active_devices: DeviceItem[];
  updated_by_user: User | null;
  created_by_user: User | null;
}

export interface ChannelList {
  total: number;
  skip: number;
  limit: number;
  items: ChannelItem[];
}

// export interface DevicesList {
//   total: number;
//   skip: number;
//   limit: number;
//   items: DeviceItem[];
// }
export interface DeviceList {
  items: DeviceItem[];
  total: number;
}

export enum FormType {
  Create = 'create',
  Edit = 'edit',
}

export enum Role {
  User = 'user',
  Admin = 'admin',
  PropertyAdmin = 'property_admin',
  SuperAdmin = 'super_admin',
}
export interface MovieItem {
  id: string;
  title: string;
  description: string;
  release_date: string | null;
  duration_minutes: number;
  actors: string[];
  imdb_id: string;
  imdb_rating: number;
  age_rating: string | null;
  stream_url: string;
  trailer_url: string;
  tags: string[];
  poster_url: string;
  artwork_url: string;
  is_enabled: boolean;
  property_ids: string[];
  is_pinned?: boolean;
}
export interface MovieList {
  total: number;
  skip: number;
  limit: number;
  items: MovieItem[];
}
export interface SeriesItem {
  id: string;
  title: string;
  description: string;
  tags?: string[];
  actors?: string[];
  poster_url: string;
  artwork_url: string;
  is_enabled: boolean;
  property_ids?: string[];
  releaseDate?: string;
  season_count?: number;
  episode_count?: number;
  imdb_id?: string;
  imdb_rating?: number;
  seasons?: SeasonItem[];
  is_pinned?: boolean;
}
export interface SeriesList {
  total: number;
  skip: number;
  limit: number;
  items: SeriesItem[];
}
export interface SeasonItem {
  id: string;
  series_id: string;
  season_number: number;
  title: string;
  description?: string;
  release_date?: string;
  is_enabled: boolean;
  poster_url?: string;
  episode_count?: number;
  created_at?: string;
  updated_at?: string;
  episodes?: EpisodeItem[];
  seasons?: SeasonItem[];
}

export interface SeasonList {
  items: SeasonItem[];
  total: number;
}
export interface EpisodeItem {
  id: string;
  season_id: string;
  series_id: string;
  episode_number: number;
  title: string;
  description?: string;
  duration_seconds?: number;
  release_date?: string;
  is_enabled: boolean;
  poster_url?: string;
  stream_url?: string;
  created_at?: string;
  updated_at?: string;

  imdb_rating?: number;
  trailer_url?: string;
  actors?: string[];
  tags?: string[];
}

export interface EpisodeList {
  items: EpisodeItem[];
  total: number;
}

export interface Organization {
  id: string;
  name: string;
  status: 'enable' | 'disable';
  desc?: string;
  order?: number;
  children?: Organization[];
}

export interface Permission {
  id: string;
  parentId: string;
  name: string;
  label: string;
  type: PermissionType;
  route: string;
  status?: BasicStatus;
  order?: number;
  icon?: string;
  component?: string;
  hide?: boolean;
  hideTab?: boolean;
  frameSrc?: URL;
  newFeature?: boolean;
  children?: Permission[];
}
